<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelDropdown from '../captain/components/ModelDropdown.vue';
import CredentialDropdown from '../captain/components/CredentialDropdown.vue';
import LlmCredentialDialog from '../captain/components/LlmCredentialDialog.vue';
import {
  providerName,
  resolveFeatureCredential,
} from '../captain/llmCredentials';

// Everything about transcribing voice messages in one place: whether it runs, when, and which model
// and LLM it runs on - with the LLM registered right here when the account has none that fits.
const FEATURE = 'audio_transcription';

const { t } = useI18n();
const route = useRoute();
const { currentAccount, updateAccount } = useAccount();
const configStore = useCaptainConfigStore();
const credentialStore = useLlmCredentialStore();
const { features } = storeToRefs(configStore);
const { records } = storeToRefs(credentialStore);

const isLoading = ref(true);
const isEnabled = ref(false);
const mode = ref('auto');
const dialogRef = ref(null);

watch(
  currentAccount,
  () => {
    const { audio_transcriptions: enabled, audio_transcription_mode: saved } =
      currentAccount.value?.settings || {};
    isEnabled.value = !!enabled;
    mode.value = saved || 'auto';
  },
  { deep: true, immediate: true }
);

const feature = computed(() => features.value[FEATURE]);
const credential = computed(() =>
  resolveFeatureCredential(records.value, feature.value)
);
const provider = computed(() => providerName(feature.value?.provider));
const modelName = computed(() => {
  const selected = feature.value?.selected || feature.value?.default;
  return (
    feature.value?.models?.find(model => model.id === selected)?.display_name ||
    selected
  );
});

const modeOptions = computed(() => [
  {
    value: 'auto',
    icon: 'i-lucide-zap',
    label: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.MODE.AUTO'),
    help: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.MODE.AUTO_HELP'),
  },
  {
    value: 'manual',
    icon: 'i-lucide-hand',
    label: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.MODE.MANUAL'),
    help: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.MODE.MANUAL_HELP'),
  },
]);

// What happens to the next voice message, worst problem first.
const status = computed(() => {
  if (!isEnabled.value) {
    return {
      tone: 'off',
      icon: 'i-lucide-audio-lines',
      text: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.OFF'),
    };
  }
  if (!credential.value) {
    return {
      tone: 'warning',
      icon: 'i-lucide-key-round',
      text: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.NO_LLM', {
        provider: provider.value,
      }),
      action: 'add',
    };
  }
  const { name } = credential.value;
  if (credential.value.limit_reached) {
    return {
      tone: 'warning',
      icon: 'i-lucide-gauge',
      text: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.LIMIT', {
        name,
      }),
      action: 'edit',
    };
  }
  if (credential.value.last_error_at) {
    return {
      tone: 'error',
      icon: 'i-lucide-triangle-alert',
      text: t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.FAILING', {
        provider: provider.value,
        name,
        error: credential.value.last_error_message,
      }),
      action: 'edit',
    };
  }
  const params = { model: modelName.value, name };
  return {
    tone: 'ok',
    icon: 'i-lucide-circle-check',
    text:
      mode.value === 'manual'
        ? t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.MANUAL', params)
        : t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.AUTO', params),
  };
});

const STATUS_CLASSES = {
  off: 'bg-n-solid-1 outline-n-weak text-n-slate-11',
  ok: 'bg-n-teal-2 outline-n-teal-5 text-n-teal-11',
  warning: 'bg-n-amber-2 outline-n-amber-5 text-n-amber-11',
  error: 'bg-n-ruby-2 outline-n-ruby-5 text-n-ruby-11',
};

const llmsRoute = computed(() => ({
  name: 'captain_settings_index',
  params: { accountId: route.params.accountId },
}));

const saveSettings = async settings => {
  try {
    await updateAccount(settings);
    useAlert(t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.API.SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.API.ERROR'));
  }
};

const toggle = () =>
  saveSettings({
    audio_transcriptions: isEnabled.value,
    audio_transcription_mode: mode.value,
  });

const changeMode = value => {
  if (value === mode.value) return;
  mode.value = value;
  saveSettings({ audio_transcription_mode: value });
};

// A model or LLM change moves which LLM pays for transcription, so the LLMs are read again.
const savePreferences = async preferences => {
  try {
    await configStore.updatePreferences(preferences);
    credentialStore.fetch();
    useAlert(t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.API.SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.API.ERROR'));
    configStore.fetch();
  }
};

const changeModel = ({ model }) =>
  savePreferences({ captain_models: { [FEATURE]: model } });

const changeCredential = ({ credential: id }) =>
  savePreferences({ captain_credentials: { [FEATURE]: id } });

const addLlm = () =>
  dialogRef.value.open(null, { provider: feature.value?.provider });

const editLlm = () => dialogRef.value.open(credential.value);

// An LLM registered from this page is the one transcription is meant to run on.
const onLlmSaved = record => {
  if (
    record.provider === feature.value?.provider &&
    record.id !== credential.value?.id
  ) {
    changeCredential({ credential: record.id });
  } else {
    credentialStore.fetch();
  }
};

const onStatusAction = () =>
  status.value.action === 'add' ? addLlm() : editLlm();

onMounted(async () => {
  try {
    await Promise.all([configStore.fetch(), credentialStore.fetch()]);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.TITLE')"
        :description="t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.DESCRIPTION')"
        :back-button-label="t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-1">
        <div
          class="flex flex-col gap-3 p-4 mb-4 sm:flex-row sm:items-center rounded-xl outline outline-1 -outline-offset-1"
          :class="STATUS_CLASSES[status.tone]"
        >
          <Icon :icon="status.icon" class="flex-shrink-0 size-5" />
          <p class="flex-1 m-0 text-sm text-n-slate-12">{{ status.text }}</p>
          <NextButton
            v-if="status.action"
            type="button"
            sm
            :faded="status.action === 'edit'"
            :slate="status.action === 'edit'"
            :icon="
              status.action === 'add' ? 'i-lucide-plus' : 'i-lucide-pencil'
            "
            :label="
              status.action === 'add'
                ? t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.ADD_LLM')
                : t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.STATUS.EDIT_LLM')
            "
            class="flex-shrink-0"
            @click="onStatusAction"
          />
        </div>

        <SectionLayout
          :title="t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENABLE.TITLE')"
          :description="
            t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENABLE.DESCRIPTION')
          "
        >
          <template #headerActions>
            <div class="flex justify-end">
              <Switch v-model="isEnabled" @change="toggle" />
            </div>
          </template>
          <div v-if="isEnabled" class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.MODE.LABEL') }}
            </span>
            <div class="grid gap-2 sm:grid-cols-2">
              <button
                v-for="option in modeOptions"
                :key="option.value"
                type="button"
                class="flex items-start gap-3 p-4 text-left transition-colors rounded-xl outline outline-1 -outline-offset-1"
                :class="
                  mode === option.value
                    ? 'bg-n-blue-2 outline-n-blue-8'
                    : 'bg-n-alpha-1 outline-n-weak hover:outline-n-slate-6'
                "
                @click="changeMode(option.value)"
              >
                <Icon
                  :icon="option.icon"
                  class="flex-shrink-0 mt-0.5 size-4"
                  :class="
                    mode === option.value ? 'text-n-blue-11' : 'text-n-slate-11'
                  "
                />
                <span class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ option.label }}
                  </span>
                  <span class="text-sm text-n-slate-11">{{ option.help }}</span>
                </span>
              </button>
            </div>
          </div>
        </SectionLayout>

        <SectionLayout
          :title="t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.TITLE')"
          :description="
            t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.DESCRIPTION')
          "
          with-border
        >
          <div class="flex flex-col gap-4">
            <div class="flex flex-wrap items-end gap-3">
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{
                    t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.MODEL')
                  }}
                </span>
                <ModelDropdown :feature-key="FEATURE" @change="changeModel" />
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.LLM') }}
                </span>
                <CredentialDropdown
                  :feature-key="FEATURE"
                  @change="changeCredential"
                />
              </div>
              <NextButton
                type="button"
                faded
                slate
                icon="i-lucide-plus"
                :label="
                  t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.ADD_LLM')
                "
                @click="addLlm"
              />
            </div>
            <router-link
              :to="llmsRoute"
              class="flex items-center gap-1 text-sm w-fit text-n-slate-11 hover:text-n-slate-12"
            >
              {{
                t('INTEGRATION_SETTINGS.AUDIO_TRANSCRIPTION.ENGINE.ALL_LLMS')
              }}
              <span class="i-lucide-arrow-up-right size-4" />
            </router-link>
          </div>
        </SectionLayout>
      </div>

      <LlmCredentialDialog ref="dialogRef" @saved="onLlmSaved" />
    </template>
  </SettingsLayout>
</template>
